import { Card, Grid, Divider, TextField, Switch, FormControlLabel } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Loading, Notify } from 'notiflix';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';
import usePermission from 'hooks/usePermission';
import CustomerRules from './CustomerRules';

// Admin → Settings → Missed Payments. What happens to a customer's service when a
// recurring payment is not collected (e.g. they switched off auto-debit at their
// bank). The day counts are measured from the payment's due date; paying at any
// point before cancellation restores the service. Reminder emails are configured
// separately in the reminder settings.

const STAGES = [
  {
    name: 'pauseAfterDays',
    label: 'Pause the subscription after (days)',
    helper:
      'Customer can still log in, see invoices and pay. No new work starts and the project tasks on Elevate stop.',
  },
  {
    name: 'onHoldAfterDays',
    label: 'Put the service on hold after (days)',
    helper: 'Same as paused, and support and delivery stop too. The subscription itself stays "paused".',
  },
  {
    name: 'cancelAfterDays',
    label: 'Cancel the subscription after (days)',
    helper:
      'Cancelled for non-payment, on the payment gateway too. Paying later does not restore it — the customer places a new order.',
  },
];

const MissedPaymentSettings = () => {
  const { user } = useSelector((state) => state.auth);
  const canUpdate = usePermission('settings', 'update');
  const authHeader = { headers: { Authorization: `Bearer ${user?.accessToken}` } };
  const [form, setForm] = useState({ enabled: false, pauseAfterDays: '', onHoldAfterDays: '', cancelAfterDays: '' });
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // reloads the customer rules after a global save

  const load = async () => {
    try {
      Loading.circle('Loading settings...');
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/missed-payment-settings`, authHeader);
      setForm({ ...data.data.settings });
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to load missed payment settings');
    } finally {
      Loading.remove();
    }
  };

  useEffect(() => {
    load();
  }, []);

  const pause = Number(form.pauseAfterDays);
  const hold = Number(form.onHoldAfterDays);
  const cancel = Number(form.cancelAfterDays);
  const ordered = pause > 0 && pause < hold && hold < cancel;

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put(
        `${ENDPOINT}api/v1/admin/missed-payment-settings`,
        { ...form, pauseAfterDays: pause, onHoldAfterDays: hold, cancelAfterDays: cancel },
        authHeader,
      );
      setForm({ ...data.data.settings });
      setRefreshKey((k) => k + 1);
      Notify.success('Saved — applies from the next hourly check');
    } catch (error) {
      console.error(error);
      Notify.failure(error.response?.data?.message || 'Failed to save missed payment settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h5" mb={1}>
                Missed Payment Settings
              </MDTypography>
              <MDTypography variant="caption" color="secondary">
                When a renewal is not collected, the customer can pay it from Desteliq or Elevate. If
                it stays unpaid, the service is paused, put on hold and finally cancelled after the
                number of days below, counted from the payment&apos;s due date. Paying at any point
                before cancellation restores the service and keeps the original billing dates.
              </MDTypography>
              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.enabled)}
                    disabled={!canUpdate}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                }
                label="Automatically pause, hold and cancel unpaid subscriptions"
              />
              <MDTypography variant="caption" color="secondary" display="block" mb={1}>
                Off = customers can still pay a missed invoice, but nothing is paused or cancelled.
                Only payments that go missing after this feature was released are affected.
              </MDTypography>

              {STAGES.map(({ name, label, helper }) => (
                <TextField
                  key={name}
                  type="number"
                  label={label}
                  value={form[name] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                  disabled={!canUpdate}
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 1, max: 365 }}
                  InputLabelProps={{ shrink: true }}
                  helperText={helper}
                />
              ))}

              {!ordered && (
                <MDTypography variant="caption" color="error" display="block">
                  Days must increase: pause &lt; on hold &lt; cancel.
                </MDTypography>
              )}

              {canUpdate && (
                <MDBox mt={3}>
                  <MDButton variant="gradient" color="info" disabled={saving || !ordered} onClick={handleSave}>
                    {saving ? 'Saving...' : 'Save'}
                  </MDButton>
                </MDBox>
              )}
            </Card>
          </Grid>
          <Grid item xs={12}>
            <CustomerRules globalPolicy={form} refreshKey={refreshKey} />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default MissedPaymentSettings;
